import { Centrifuge } from "centrifuge";
import { PushClientSettings } from "types/generated/models";

interface TokenResponse {
    token: string;
}

const CHANNEL_LIMIT = 10;
const DEFAULT_TIMEOUT_MS = 60_000;

export interface StripePushPaymentData {
    isSuccess: boolean;
    paymentIntentId: string;
    errorMessage?: string;
    paymentId?: number;
}

interface StripePushPublication {
    data: {
        data: StripePushPaymentData;
        type: string;
    };
    offset?: number;
}

type ReadyPushClientSettings = PushClientSettings & {
    url: string;
    token: string;
};

function isReadyPushClientSettings(
    settings: PushClientSettings | null | undefined
): settings is ReadyPushClientSettings {
    return !!settings?.url && !!settings.token;
}

function createCentrifuge(pushSettings: ReadyPushClientSettings): Centrifuge {
    const refreshEndpoint = pushSettings.refreshEndpoint;

    async function getToken(): Promise<string> {
        const res = await fetch(refreshEndpoint);
        if (!res.ok) {
            if (res.status === 403) {
                const UnauthorizedErrorCtor = Centrifuge.UnauthorizedError as new (message: string) => Error;
                throw new UnauthorizedErrorCtor("Unauthorized");
            }
            throw new Error(`Unexpected status code ${res.status}`);
        }
        const data = (await res.json()) as TokenResponse;
        return data.token;
    }

    return new Centrifuge(pushSettings.url, {
        token: pushSettings.token,
        // Only provide a refresh callback when a refresh endpoint is configured;
        // otherwise rely on the long-lived token to avoid fetch(undefined) at runtime.
        ...(refreshEndpoint ? { getToken } : {}),
    });
}

function waitForWebhookResult(
    pushSettings: ReadyPushClientSettings,
    paymentIntentId: string,
    timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<StripePushPaymentData> {
    return new Promise((resolve, reject) => {
        const channelName = `stripe-payment/${paymentIntentId}`;
        let isSettled = false;

        const centrifuge = createCentrifuge(pushSettings);
        const subscription = centrifuge.newSubscription(channelName);

        const cleanupFn = () => {
            try {
                subscription.unsubscribe();
            } catch (error_) {
                console.error(error_);
            }
            try {
                centrifuge.disconnect();
            } catch (error_) {
                console.error(error_);
            }
        };

        const finalize = (callback: () => void) => {
            if (isSettled) {
                return;
            }
            isSettled = true;
            globalThis.clearTimeout(timeoutHandle);
            cleanupFn();
            callback();
        };

        const timeoutHandle = globalThis.setTimeout(() => {
            finalize(() => reject(new Error("Payment confirmation timed out. Please check your payment status.")));
        }, timeoutMs);

        let isInitialHistoryReplayPending = true;
        const bufferedLivePublications: StripePushPublication[] = [];

        const stopTrackingLivePublications = () => {
            isInitialHistoryReplayPending = false;
            bufferedLivePublications.length = 0;
        };

        subscription.on("publication", (ctx: StripePushPublication) => {
            if (isInitialHistoryReplayPending) {
                // Buffer all publications, including those without an offset (channel
                // namespaces without history configured do not assign sequence offsets).
                bufferedLivePublications.push(ctx);
                return;
            }
            finalize(() => resolve(ctx.data?.data));
        });

        const flushBufferedLivePublications = () => {
            // Preserve arrival order for publications without offsets, while still
            // ordering those that do carry offsets by their sequence number.
            const buffered = bufferedLivePublications
                .map((publication, index) => ({ publication, index }))
                .sort((a, b) => {
                    const aHasOffset = Number.isFinite(a.publication.offset);
                    const bHasOffset = Number.isFinite(b.publication.offset);
                    if (aHasOffset && bHasOffset) {
                        return (a.publication.offset ?? 0) - (b.publication.offset ?? 0);
                    }
                    if (aHasOffset !== bHasOffset) {
                        return aHasOffset ? -1 : 1;
                    }
                    return a.index - b.index;
                })
                .map((entry) => entry.publication);
            stopTrackingLivePublications();
            if (buffered.length > 0) {
                finalize(() => resolve(buffered[0].data?.data));
            }
            // Otherwise we keep listening for live publications via the "publication" handler.
        };

        subscription.on("subscribed", () => {
            subscription
                .history({ limit: CHANNEL_LIMIT })
                .then((response) => {
                    const { publications } = response;

                    // Flush any already-published messages from history first.
                    if (publications.length > 0) {
                        const sorted = publications.slice().sort((a, b) => (a.offset ?? 0) - (b.offset ?? 0));
                        const first = sorted[0] as StripePushPublication;
                        stopTrackingLivePublications();
                        finalize(() => resolve(first.data?.data));
                        return;
                    }

                    // No history — flush any buffered live publications that arrived
                    // while history was being fetched.
                    flushBufferedLivePublications();
                })
                .catch((error_) => {
                    console.error(error_);
                    flushBufferedLivePublications();
                });
        });

        subscription.on("error", (ctx: { error?: { message?: string; code?: number } }) => {
            const message = ctx?.error?.message ?? "Failed to subscribe to payment updates channel.";
            finalize(() => reject(new Error(message)));
        });

        subscription.subscribe();
        centrifuge.connect();
    });
}

export function useStripePushPayment() {
    const waitForPaymentWebhook = (
        pushSettings: PushClientSettings | null | undefined,
        paymentIntentId: string,
        timeoutMs?: number
    ): Promise<StripePushPaymentData> | null => {
        if (!isReadyPushClientSettings(pushSettings)) {
            return null;
        }

        return waitForWebhookResult(pushSettings, paymentIntentId, timeoutMs);
    };

    return { waitForPaymentWebhook };
}
